import {
  Blocks,
  GitBranch,
  KeyRound,
  MessageSquareMore,
  SwatchBook,
  Users,
  WalletCards,
} from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { canSeeSubscription } from "modules/subscription/lib/canSeeSubscription"
import { useTranslation } from "react-i18next"
import { SettingsCard, SettingsSection } from "./SettingsCard"

export function CompanyCards() {
  const { t } = useTranslation()
  return <SettingsCardGrid sections={buildCompanySections(t)} />
}

export function OrganizationCards() {
  const { t } = useTranslation()

  const { data: employee } = useAuthEmployeeQuery()
  const { data: integrationsStatus } = useIntegrationsStatusQuery()

  const sections = buildOrganizationSections(t, employee).map((s) =>
    s.id === "whatsapp" ? { ...s, isActive: integrationsStatus?.whatsapp } : s,
  )
  return <SettingsCardGrid sections={sections} />
}

export function AllCardsUnderCompany() {
  const { t } = useTranslation()
  const { data: employee } = useAuthEmployeeQuery()
  const { data: integrationsStatus } = useIntegrationsStatusQuery()

  const companySections = buildCompanySections(t)
  const organizationSections =
    employee?.isOwner || employee?.isManager
      ? buildOrganizationSections(t, employee).map((s) => ({
          ...s,
          id: `org-${s.id}`,
          ...(s.id === "whatsapp" && { isActive: integrationsStatus?.whatsapp }),
        }))
      : []

  return <SettingsCardGrid sections={[...companySections, ...organizationSections]} />
}

function buildCompanySections(t: ReturnType<typeof useTranslation>["t"]): SettingsSection[] {
  return [
    {
      id: "customization",
      title: t("settings.company.customization.title"),
      description: t("settings.company.customization.description"),
      icon: SwatchBook,
      navigate: { to: "/settings/customization" },
    },
    {
      id: "channels",
      title: t("settings.channels.title"),
      description: t("settings.channels.description"),
      icon: MessageSquareMore,
      navigate: { to: "/settings/channels" },
    },
    {
      id: "integrations",
      title: t("settings.integrations.title"),
      description: t("settings.integrations.description"),
      icon: Blocks,
      navigate: { to: "/settings/integrations" },
    },
    {
      id: "workflows",
      title: t("settings.workflows.title"),
      description: t("settings.workflows.description"),
      icon: GitBranch,
      navigate: { to: "/settings/workflows" },
    },
    {
      id: "keysAndSecrets",
      title: t("settings.keysAndSecrets.title"),
      description: t("settings.keysAndSecrets.description"),
      icon: KeyRound,
      navigate: { to: "/settings/keys-and-secrets" },
    },
  ]
}

function buildOrganizationSections(
  t: ReturnType<typeof useTranslation>["t"],
  employee: ReturnType<typeof useAuthEmployeeQuery>["data"],
): SettingsSection[] {
  const showPlans = canSeeSubscription(employee)

  return [
    showPlans &&
      ({
        id: "plans",
        title: t("subscription.title"),
        description: t("subscription.description"),
        icon: WalletCards,
        navigate: { to: "/settings/subscription" },
      } as const),
    {
      id: "team",
      title: t("settings.team.title"),
      description: t("settings.team.description"),
      icon: Users,
      navigate: { to: "/settings/team" },
    } as const,
  ].filter(Boolean) as SettingsSection[]
}

function SettingsCardGrid({ sections }: { sections: SettingsSection[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {sections.map((section) => {
        return <SettingsCard key={section.id} section={section} />
      })}
    </div>
  )
}
